import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, X, Ban, Gift, ImageIcon, Video, Pencil, Sparkles, Monitor, Headphones, TrendingDown, Target, Clapperboard, MessageCircle, Users, BarChart3, BrainCircuit, Send, Tag, ChevronRight, ChevronDown } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';

// Define our subscription plan types
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const Paywall = () => {
  const navigate = useNavigate();
  const { setMobileTitleOverride, setMobileBackOverride } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [activePlanIndex, setActivePlanIndex] = useState<number>(1); // Default to Negocio plan (middle)
    const [promoMs, setPromoMs] = useState<number>(0);
  const [shortName, setShortName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [showPromoCountdown, setShowPromoCountdown] = useState<boolean>(false);
  
  const [cancellingSubscription, setCancellingSubscription] = useState<boolean>(false);
  const [cancelSuccess, setCancelSuccess] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [cancelStep, setCancelStep] = useState<1 | 2 | 3 | 4>(1);
  const [cancelMotivo, setCancelMotivo] = useState<string>('');
  const [cancelDetalle, setCancelDetalle] = useState<string>('');
  const [cancelSuccessDate, setCancelSuccessDate] = useState<string>('');
  const [showContactQR, setShowContactQR] = useState<string>('');
  const [fechaCancelacion, setFechaCancelacion] = useState<string | null>(null);

  // Ultra plan tier selector (Argentina only: 3 tiers)
  const [ultraTierIndex, setUltraTierIndex] = useState<number>(0); // default to 500 images tier
  const [ultraTierOpen, setUltraTierOpen] = useState(false);
  const ultraTierRefMobile = useRef<HTMLDivElement>(null);
  const ultraTierRefDesktop = useRef<HTMLDivElement>(null);
  const ultraTiers = [
    { label: '500 imágenes', images: 500, videos: 40, price: 109999, planId: 'enterprise1' },
    { label: '1.000 imágenes', images: 1000, videos: 80, price: 209999, planId: 'enterprise2' },
    { label: '2.000 imágenes', images: 2000, videos: 160, price: 399999, planId: 'enterprise3' },
  ];

  // Coupon state
  const [showCouponInput, setShowCouponInput] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<{ id: string; codigo: string; multiplicador: number; es_invisible?: boolean } | null>(null);

  // Close ultra tier dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      const inMobile = ultraTierRefMobile.current?.contains(t);
      const inDesktop = ultraTierRefDesktop.current?.contains(t);
      if (!inMobile && !inDesktop) setUltraTierOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Override sidebar mobile title so it doesn't show "Estudio virtual"
  useEffect(() => {
    setMobileTitleOverride(' ');
    return () => { setMobileTitleOverride(null); };
  }, [setMobileTitleOverride]);

  // Country/provider settings
  const [pais, setPais] = useState<string>('');
  const normalizedPais = (pais || '').toLowerCase();
  const isArgentina = normalizedPais === 'argentina';
  const isChile = normalizedPais === 'chile';
  const isColombia = normalizedPais === 'colombia';
  const isMexico = normalizedPais === 'méxico' || normalizedPais === 'mexico';
  const isPeru = normalizedPais === 'perú' || normalizedPais === 'peru';

  const currencyCode = isArgentina
    ? 'ARS'
    : isChile
    ? 'CLP'
    : isColombia
    ? 'COP'
    : isMexico
    ? 'MXN'
    : isPeru
    ? 'PEN'
    : 'USD';

  const providerName = isArgentina ? 'Mercado Pago' : 'Stripe';
  const getPlanPrice = (planId: string, arsPrice: number) => {
    if (isArgentina) return arsPrice;

    if (isChile) {
      switch (planId) {
        case 'emprendimiento': return 14999;
        case 'negocio': return 49999;
        case 'enterprise': case 'enterprise1': return 79999;
        case 'enterprise2': return 149999;
        case 'enterprise3': return 299999;
        default: return 0;
      }
    }

    if (isColombia) {
      switch (planId) {
        case 'emprendimiento': return 59999;
        case 'negocio': return 199000;
        case 'enterprise': case 'enterprise1': return 299999;
        case 'enterprise2': return 549999;
        case 'enterprise3': return 1099999;
        default: return 0;
      }
    }

    if (isMexico) {
      switch (planId) {
        case 'emprendimiento': return 299;
        case 'negocio': return 999;
        case 'enterprise': case 'enterprise1': return 1699;
        case 'enterprise2': return 2999;
        case 'enterprise3': return 5999;
        default: return 0;
      }
    }

    if (isPeru) {
      switch (planId) {
        case 'emprendimiento': return 49;
        case 'negocio': return 179;
        case 'enterprise': case 'enterprise1': return 299;
        case 'enterprise2': return 549;
        case 'enterprise3': return 1099;
        default: return 0;
      }
    }

    // Default: USD
    switch (planId) {
      case 'emprendimiento': return 14;
      case 'negocio': return 49;
      case 'enterprise': case 'enterprise1': return 79;
      case 'enterprise2': return 149;
      case 'enterprise3': return 299;
      default: return 0;
    }
  };

  const formatPrice = (amount: number) => {
    const locale = isArgentina
      ? 'es-AR'
      : isChile
      ? 'es-CL'
      : isColombia
      ? 'es-CO'
      : isMexico
      ? 'es-MX'
      : isPeru
      ? 'es-PE'
      : 'en-US';

    const fmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 0 });
    return `$${fmt.format(amount)} ${currencyCode}`;
  };

  // Plan hierarchy for determining upgrades / downgrades
  const planOrder: Record<string, number> = { gratis: 0, emprendimiento: 1, negocio: 2, enterprise: 3, enterprise1: 3.1, enterprise2: 3.2, enterprise3: 3.3, experto: 4 };
  const isUpgrade = (planId: string) => planOrder[planId] > (planOrder[currentPlan] ?? 0);
  const isDowngrade = (planId: string) => planOrder[planId] < (planOrder[currentPlan] ?? 0);
  const isEnterprisePlan = (plan: string) => plan === 'enterprise' || plan === 'enterprise1' || plan === 'enterprise2' || plan === 'enterprise3';
  // Resolve 'enterprise' card id to the actual selected tier planId for price lookups
  const effectivePlanId = (planId: string) => planId === 'enterprise' ? ultraTiers[ultraTierIndex].planId : planId;
  // Check if a card represents the user's current plan
  const isCurrentPlan = (cardId: string) => {
    if (cardId === 'enterprise') return isEnterprisePlan(currentPlan);
    return currentPlan === cardId;
  };

  // Plans configuration
  const plans: SubscriptionPlan[] = [
    {
      id: "emprendimiento",
      name: "Básico",
      price: 21999,
      description: "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
      features: [
        "Hasta 60 imágenes por mes",
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "~Generación de videos con IA"
      ],
      max_fotos_mes: 60
    },
    {
      id: "negocio",
      name: "Pro",
      price: 69999,
      description: "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
      features: [
        "Hasta 250 imágenes por mes",
        "Hasta 20 videos por mes",
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "Soporte prioritario"
      ],
      max_fotos_mes: 250
    },
    // When user already has an enterprise plan, show individual tier cards
    // Otherwise show a single Ultra card with dropdown selector
    ...(isEnterprisePlan(currentPlan)
      ? ultraTiers.map(tier => ({
          id: tier.planId,
          name: `Ultra Nivel ${ultraTiers.indexOf(tier) + 1}`,
          price: tier.price,
          description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
          features: [
            `Hasta ${tier.images.toLocaleString('es-AR')} imágenes por mes`,
            `Hasta ${tier.videos} videos por mes`,
            "Función de Edición de imágenes con IA",
            "Acceso desde computadora, tablet y celular",
            "Soporte dedicado 24/7",
            "Mejor relación precio / imagen",
            "Asesoría estratégica para campañas"
          ],
          max_fotos_mes: tier.images
        }))
      : [{
          id: "enterprise",
          name: "Ultra",
          price: ultraTiers[ultraTierIndex].price,
          description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
          features: [
            `Hasta ${ultraTiers[ultraTierIndex].images.toLocaleString('es-AR')} imágenes por mes`,
            `Hasta ${ultraTiers[ultraTierIndex].videos} videos por mes`,
            "Función de Edición de imágenes con IA",
            "Acceso desde computadora, tablet y celular",
            "Soporte dedicado 24/7",
            "Mejor relación precio / imagen",
            "Asesoría estratégica para campañas"
          ],
          max_fotos_mes: ultraTiers[ultraTierIndex].images
        }]
    ),
    {
      id: "experto",
      name: "Empresa",
      price: 0,
      description: "Dale al marketing de tu empresa contenido IA sin límites",
      features: [
        "Manager de redes dedicado",
        "Estrategia de contenido personalizada",
        "Publicaciones y gestión diaria",
        "Reportes de rendimiento mensuales",
        "Imágenes y videos con IA incluidos",
        "Soporte dedicado 24/7"
      ],
      max_fotos_mes: 0
    }
  ];

  // Auto-load Hot Sale coupon (Argentina only, after profile loads)
  useEffect(() => {
    if (loading || !isArgentina) return;
    const loadHotSaleCoupon = async () => {
      try {
        const { data, error: dbError } = await supabase
          .from('cupones')
          .select('id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible')
          .eq('id', '9e0abd0d-5e0a-4f84-8036-b425f7176207')
          .single();
        if (!dbError && data && data.activo) {
          const expired = data.fecha_expiracion && new Date(data.fecha_expiracion) < new Date();
          const maxedOut = data.usos_maximos !== null && data.usos_actuales >= data.usos_maximos;
          if (!expired && !maxedOut) {
            setAppliedCoupon({ id: data.id, codigo: data.codigo, multiplicador: data.multiplicador, es_invisible: true });
          }
        }
      } catch {}
    };
    loadHotSaleCoupon();
  }, [loading, isArgentina]);

  useEffect(() => {
    fetchUserProfile();
    
    // Ensure all desktop cards have the same height
    const timer = setTimeout(() => equalizeCardHeights(), 100);
    const timer2 = setTimeout(() => equalizeCardHeights(), 400);
    const timer3 = setTimeout(() => equalizeCardHeights(), 800);
    
    window.addEventListener('resize', equalizeCardHeights);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', equalizeCardHeights);
    };
  }, []);

  // Start a simple 57:00 countdown when page loads (non-persistent across navigations)
  useEffect(() => {
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs(prev => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  
  
  // Function to ensure all cards have equal heights (desktop only)
  const equalizeCardHeights = () => {
    // Only equalize on desktop (md breakpoint = 768px)
    if (window.innerWidth < 768) return;
    
    const cards = document.querySelectorAll('.plan-card');
    if (cards.length === 0) return;
    
    // Reset heights first
    cards.forEach(card => {
      (card as HTMLElement).style.height = 'auto';
      (card as HTMLElement).style.minHeight = '0';
    });
    
    // Find the tallest card
    let maxHeight = 0;
    cards.forEach(card => {
      const height = card.getBoundingClientRect().height;
      maxHeight = Math.max(maxHeight, height);
    });
    
    const h = Math.ceil(maxHeight);
    // Apply the height to all cards
    cards.forEach(card => {
      (card as HTMLElement).style.height = `${h}px`;
      (card as HTMLElement).style.minHeight = `${h}px`;
    });
  };

  const featureIcon = (text: string, disabled: boolean) => {
    const cls = `w-4 h-4 ${disabled ? 'text-neutral-300' : 'text-neutral-600'} mr-1.5 md:mr-2 mt-0.5 flex-shrink-0`;
    if (disabled) return <X className={cls} />;
    if (text.startsWith('Hasta ') && text.includes('imágenes por mes')) return <ImageIcon className={cls} />;
    if (text.includes('Videos pro')) return <Clapperboard className={cls} />;
    if (text.includes('Generación de videos')) return <Video className={cls} />;
    if (text.includes('Edición de imágenes')) return <Pencil className={cls} />;
    if (text.includes('Modo Pro')) return <Sparkles className={cls} />;
    if (text.includes('computadora, tablet')) return <Monitor className={cls} />;
    if (text.includes('Soporte')) return <Headphones className={cls} />;
    if (text.includes('relación precio')) return <TrendingDown className={cls} />;
    if (text.includes('Asesoría')) return <Target className={cls} />;
    if (text.includes('Manager de redes')) return <Users className={cls} />;
    if (text.includes('Estrategia de contenido')) return <Target className={cls} />;
    if (text.includes('Publicaciones') && text.includes('gestión')) return <Send className={cls} />;
    if (text.includes('Reportes de rendimiento')) return <BarChart3 className={cls} />;
    if (text.includes('Imágenes y videos con IA')) return <BrainCircuit className={cls} />;
    return <CheckCircle2 className={cls} />;
  };

  // Update mobile nav bar back button behavior based on cancel step
  useEffect(() => {
    if (!showCancelModal) {
      setMobileBackOverride(null);
      return;
    }
    if (cancelStep === 1) {
      setMobileBackOverride(() => () => handleCloseCancelModal());
    } else if (cancelStep === 2) {
      setMobileBackOverride(() => () => { setCancelStep(1); setCancelDetalle(''); });
    } else if (cancelStep === 3) {
      setMobileBackOverride(() => () => setCancelStep(2));
    } else if (cancelStep === 4) {
      setMobileBackOverride(() => () => { handleCloseCancelModal(); fetchUserProfile(); });
    }
  }, [showCancelModal, cancelStep]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id_company, name_short')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      
      if (!profileData.id_company) throw new Error('No hay una empresa asociada a este usuario');
      
      const { data: company, error: companyError } = await supabase
        .from('company_profiles')
        .select('plan, max_fotos_mes, pais, nombre, imagenes_totales_generadas, fecha_cancelacion_programada, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', profileData.id_company)
        .single();
        
      if (companyError) throw companyError;
      
      setProfile({
        ...profileData,
        user_id: user.id,
        __paid_plan_start_date: (company as any)?.paid_plan_start_date || null,
        __upgrade_plan_date: (company as any)?.upgrade_plan_date || null,
      });
      if ((profileData as any)?.name_short) setShortName((profileData as any).name_short as string);
      if ((company as any)?.nombre) setCompanyName((company as any).nombre as string);
      
      setCurrentPlan(company.plan);
      if ((company as any)?.pais) setPais((company as any).pais as string);
      if ((company as any)?.fecha_cancelacion_programada) setFechaCancelacion((company as any).fecha_cancelacion_programada as string);

      const total = (company as any)?.imagenes_totales_generadas as number | undefined;
      setShowPromoCountdown(typeof total === 'number' ? total < 22 : false);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Error al cargar la información del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponError('Ingresá un código de cupón'); return; }
    try {
      setCouponLoading(true);
      setCouponError(null);
      const { data, error: dbError } = await supabase
        .from('cupones')
        .select('id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible')
        .eq('codigo', code)
        .single();
      if (dbError || !data) { setCouponError('Cupón no encontrado'); return; }
      if (!data.activo) { setCouponError('Este cupón ya no está activo'); return; }
      if (data.usos_maximos !== null && data.usos_actuales >= data.usos_maximos) { setCouponError('Este cupón ya alcanzó su límite de usos'); return; }
      if (data.fecha_expiracion && new Date(data.fecha_expiracion) < new Date()) { setCouponError('Este cupón ha expirado'); return; }
      setAppliedCoupon({ id: data.id, codigo: data.codigo, multiplicador: data.multiplicador, es_invisible: !!data.es_invisible });
      setShowCouponInput(false);
      setCouponCode('');
    } catch {
      setCouponError('Error al validar el cupón');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubscription = async (planId: string) => {
    try {
      setProcessing(true);
      setError(null);
      
      const selectedPlan = plans.find(plan => plan.id === planId);
      if (!selectedPlan) throw new Error('Plan no encontrado');
      
      // For Ultra plan with tier selector, send the specific tier planId
      const actualPlanId = planId === 'enterprise' ? ultraTiers[ultraTierIndex].planId : planId;
      
      // All countries: navigate to dedicated checkout page, pass coupon if applied
      navigate('/checkout', { state: { planId: actualPlanId, pais, coupon: appliedCoupon || undefined, currentPlan } });
      return;
      
    } catch (err: any) {
      console.error('Error processing subscription:', err);
      setError(err.message || 'Error al procesar la suscripción');
    } finally {
      setProcessing(false);
    }
  };
  
  // Cancel flow reasons
  const cancelReasons = [
    'La calidad de las imágenes no cumplió mis expectativas',
    'La calidad de los videos no cumplió mis expectativas',
    'Las imágenes no están dando los resultados que esperaba',
    'Los videos no están dando los resultados que esperaba',
    'Quiero pausar mi suscripción por ahora',
    'El plan es demasiado caro',
    'Ya no necesito crear imágenes',
    'Ya no necesito crear videos',
    'Me cambié a otra herramienta',
    'La herramienta es muy complicada para mí',
    'Otro',
  ];

  // Opens the cancel confirmation modal
  const handleOpenCancelModal = () => {
    setCancelStep(1);
    setCancelMotivo('');
    setCancelDetalle('');
    setCancelSuccessDate('');
    setShowCancelModal(true);
    setMobileBackOverride(() => () => handleCloseCancelModal());
  };

  // Close the cancel confirmation modal
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelStep(1);
    setCancelMotivo('');
    setCancelDetalle('');
    setMobileBackOverride(null);
  };

  // Calculate next renewal date for the success message
  const getNextRenewalDate = (): string => {
    const paidStart = (profile as any)?.__paid_plan_start_date;
    const upgradeDate = (profile as any)?.__upgrade_plan_date;
    if (!paidStart) {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    const now = new Date();
    const subscriptionDate = new Date(paidStart);
    const referenceDate = upgradeDate ? new Date(upgradeDate) : subscriptionDate;
    const anchorDay = referenceDate.getDate();
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return end.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  // Handler for cancel subscription button after confirmation
  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      setError(null);
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Send request to the cancel subscription endpoint
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/ba02b716-44a0-44e7-a3b7-72c3f5ccfbcd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          motivo: cancelMotivo,
          detalle: cancelDetalle,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al cancelar la suscripción');
      }
      
      const data = await response.json();
      
      if (data.response === 'success') {
        // Calculate renewal date and show success step
        setCancelSuccessDate(getNextRenewalDate());
        setCancelStep(4);
        setCancelSuccess(true);
      } else {
        throw new Error('Error al procesar la cancelación');
      }
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'Error al cancelar la suscripción');
      setShowCancelModal(false);
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-2 md:p-8 w-full max-w-full overflow-x-hidden">
      {/* Mobile: title only, no back button (sidebar provides it) */}
      <div className="sm:hidden">
        <AppleHeader title={fechaCancelacion ? 'Tu plan' : currentPlan !== 'gratis' ? 'Mejora tu plan' : 'Elegí tu plan'} />
      </div>
      {/* Desktop: title + back button */}
      <div className="hidden sm:block relative">
        <AppleHeader title={fechaCancelacion ? 'Tu plan' : currentPlan !== 'gratis' ? 'Mejora tu plan' : 'Elegí tu plan'} onBack={() => navigate(-1)} backLabel="Volver" />
      </div>
      {!fechaCancelacion && <p className="text-[13px] text-neutral-500 text-center -mt-2 mb-4 sm:mb-5">Aumenta tus ventas como ya lo hicieron cientos de marcas</p>}

      {/* Hot Sale banner — desktop */}
      {!fechaCancelacion && appliedCoupon && appliedCoupon.es_invisible && (
        <div className="hidden md:flex items-center justify-center gap-8 mx-auto max-w-lg mb-0 relative z-20">
          <img src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20logo.png" alt="Hot Sale" className="h-32 object-contain" />
          <div className="text-center">
            <p className="text-[56px] font-bold leading-none" style={{ color: '#E55D35' }}>{Math.round((1 - appliedCoupon.multiplicador) * 100)}%</p>
            <p className="text-[17px] font-medium text-neutral-600">de descuento</p>
          </div>
        </div>
      )}
      {/* Fire row — desktop only, tucked behind banner (above) and cards (below) */}
      {!fechaCancelacion && appliedCoupon?.es_invisible && (
        <div className="hidden md:flex justify-center pointer-events-none -mt-8 -mb-16 relative z-0">
          {[...Array(7)].map((_, i) => (
            <img key={i} src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20fuego.png" alt="" className="h-48 object-contain -mx-2 opacity-30" />
          ))}
        </div>
      )}
      {/* Hot Sale banner — mobile */}
      {!fechaCancelacion && appliedCoupon && appliedCoupon.es_invisible && (
        <div className="mx-auto max-w-lg mb-2 md:hidden">
          <div className="flex items-center justify-center gap-3">
            <img src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20logo.png" alt="Hot Sale" className="h-28 object-contain" />
            <div className="text-center">
              <p className="text-[48px] font-bold leading-none" style={{ color: '#E55D35' }}>{Math.round((1 - appliedCoupon.multiplicador) * 100)}%</p>
              <p className="text-[17px] font-medium text-neutral-600">de descuento</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700 mx-1">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[15px] text-green-700 mx-1">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* ── Scheduled cancellation notice ── */}
      {fechaCancelacion && (() => {
        const currentPlanObj = plans.find(p => p.id === currentPlan);
        const CANCEL_PLAN_LABELS: Record<string, string> = { gratis: 'Prueba', emprendimiento: 'Básico', negocio: 'Pro', enterprise: 'Ultra', enterprise1: 'Ultra 500', enterprise2: 'Ultra 1.000', enterprise3: 'Ultra 2.000', experto: 'Empresa' };
        const planLabel = CANCEL_PLAN_LABELS[currentPlan] || currentPlan;
        const cancelDateStr = new Date(fechaCancelacion).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
        return (
          <div className="max-w-[420px] mx-auto mt-2">
            {/* Grouped card container */}
            <div className="bg-surface-card rounded-[13px] overflow-hidden">

              {/* Plan header row */}
              <div className="px-4 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center">
                    <span className="material-symbols-rounded text-brand-primary text-[17px]">workspace_premium</span>
                  </div>
                  <div>
                    <p className="text-[15px] font-semibold text-brand-black/90">Plan {planLabel}</p>
                    {currentPlanObj && (
                      <p className="text-[13px] text-neutral-500">{formatPrice(getPlanPrice(currentPlanObj.id, currentPlanObj.price))}/mes</p>
                    )}
                  </div>
                </div>
                <span className="text-[11px] font-medium text-brand-primary bg-brand-primary/[0.08] px-2 py-0.5 rounded-full">Activo</span>
              </div>

              {/* Separator */}
              <div className="mx-4 h-px bg-black/[0.06]" />

              {/* Cancellation info */}
              <div className="px-4 py-3.5">
                <div className="flex items-start gap-2.5">
                  <div className="w-9 h-9 rounded-sm bg-amber-50 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="pt-0.5">
                    <p className="text-[15px] font-medium text-brand-black/90">Cancelación programada</p>
                    <p className="text-[13px] text-neutral-500 mt-0.5">
                      Tu plan se cancelará el <strong className="text-brand-black/80">{cancelDateStr}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Separator */}
              <div className="mx-4 h-px bg-black/[0.06]" />

              {/* Info note */}
              <div className="px-4 py-3.5">
                <p className="text-[13px] text-neutral-500 leading-relaxed">
                  Hasta esa fecha seguirás teniendo acceso a todas las funciones de tu plan actual.
                </p>
              </div>

              {/* Features (only if paid plan) */}
              {currentPlanObj && (
                <>
                  <div className="mx-4 h-px bg-black/[0.06]" />
                  <div className="px-4 py-3.5">
                    <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-2.5">Tu plan incluye</p>
                    <ul className="space-y-2">
                      {currentPlanObj.features.filter(f => !f.startsWith('~')).map((feature, i) => (
                        <li key={i} className="flex items-start text-[13px] text-neutral-600">
                          {featureIcon(feature, false)}
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>

            {/* WhatsApp help — mobile: direct link, desktop: QR modal */}
            <div className="mt-4 text-center">
              <a
                href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tengo una duda sobre la cancelación de mi plan.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="md:hidden inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium active:opacity-50 transition-opacity"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                ¿Tenés alguna duda? Escribinos
              </a>
              <button
                type="button"
                onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tengo una duda sobre la cancelación de mi plan.`)}
                className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium hover:text-brand-primary-hover transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                ¿Tenés alguna duda? Escribinos
              </button>
            </div>
          </div>
        );
      })()}

      {!fechaCancelacion && (<>
      {/* Coupon section — desktop only (Argentina only) */}
      {isArgentina && <div className="hidden md:block mx-1 mb-4">
        {appliedCoupon && appliedCoupon.es_invisible ? null : appliedCoupon ? (
          <div className="max-w-lg mx-auto flex items-center justify-between p-3 bg-surface-card border border-brand-primary/20 rounded-md">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-brand-primary" />
              <span className="text-[13px] text-brand-black/90">
                Cupón <strong>{appliedCoupon.codigo}</strong> aplicado — {Math.round((1 - appliedCoupon.multiplicador) * 100)}% de descuento
              </span>
            </div>
            <button type="button" onClick={() => setAppliedCoupon(null)} className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors">Quitar</button>
          </div>
        ) : showCouponInput ? (
          <div className="max-w-sm flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
                placeholder="Ingresá tu código de cupón"
                className="flex-1 h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                autoFocus
              />
              <button type="button" onClick={handleValidateCoupon} disabled={couponLoading} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center gap-1.5">
                {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
              </button>
              <button type="button" onClick={() => { setShowCouponInput(false); setCouponCode(''); setCouponError(null); }} className="h-10 px-3 text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors">Cancelar</button>
            </div>
            {couponError && (
              <p className="text-[13px] text-red-600 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{couponError}</p>
            )}
          </div>
        ) : (
          <button type="button" onClick={() => setShowCouponInput(true)} className="flex items-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors">
            <Tag className="w-3.5 h-3.5" />
            Tengo un cupón
          </button>
        )}
      </div>}

      {/* Mobile: horizontal plan selector tabs */}
      {(() => {
        const filteredPlans = plans.filter((plan) => {
          if (plan.id === 'emprendimiento' && (currentPlan === 'negocio' || isEnterprisePlan(currentPlan))) return false;
          return true;
        });
        return (
          <>
            {/* Fire row — mobile, behind plan tabs */}
            {appliedCoupon?.es_invisible && (
              <div className="md:hidden flex justify-center pointer-events-none -mb-10 relative z-0 mt-2">
                {[...Array(5)].map((_, i) => (
                  <img key={i} src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20fuego.png" alt="" className="h-32 object-contain -mx-2 opacity-20" />
                ))}
              </div>
            )}
            <div className="md:hidden flex rounded-md bg-surface-elevated p-1 mb-4 mt-2 relative z-10">
              {filteredPlans.map((plan, idx) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setActivePlanIndex(idx)}
                  className={`flex-1 py-2 text-[13px] font-medium rounded-sm transition-colors ${
                    activePlanIndex === idx
                      ? 'bg-surface-card text-brand-black/90 shadow-sm'
                      : 'text-neutral-500'
                  }`}
                >
                  {plan.name.startsWith('Ultra Nivel') ? (
                    <span className="flex flex-col items-center leading-tight">
                      <span>Ultra</span>
                      <span>{plan.name.replace('Ultra ', '')}</span>
                    </span>
                  ) : plan.name}
                </button>
              ))}
            </div>
            {/* Mobile: sliding cards */}
            <div className="md:hidden overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${activePlanIndex * 100}%)` }}
              >
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="w-full flex-shrink-0 px-0.5"
                >
                <div
                  className={`plan-card border rounded-lg w-full bg-surface-card ${isCurrentPlan(plan.id) ? 'border-brand-primary' : 'border-neutral-200'} flex flex-col relative`}
                >
            <div className="px-5 py-4 md:p-5 flex flex-col h-full">
              <div style={{ minHeight: '210px' }} className="flex flex-col">
              <h3 className="text-[17px] font-bold text-brand-black/90">{plan.name}</h3>
              
              <p className="text-[13px] text-neutral-500 mb-4 mt-1.5">{plan.description}</p>
              
              {plan.id !== 'experto' && (
              <div className="mb-5">
                {appliedCoupon && (plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) ? (
                  <>
                    <p className="text-[17px] font-bold text-brand-black/90">
                      <span className="flex items-center justify-start gap-2">
                        <span className="text-[13px] font-normal text-neutral-400 line-through">
                          {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                        </span>
                        <span className={`text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm ${appliedCoupon?.es_invisible ? '' : 'bg-brand-primary'}`} style={appliedCoupon?.es_invisible ? { background: '#E55D35' } : undefined}>{Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF</span>
                      </span>
                      <span className="block">
                        {formatPrice(Math.floor(getPlanPrice(effectivePlanId(plan.id), plan.price) * appliedCoupon.multiplicador))}
                        <span className="text-[13px] font-normal text-neutral-500"> el primer mes</span>
                      </span>
                    </p>
                    {isArgentina && (
                      <p className="text-[11px] text-neutral-400 mt-1">
                        + {plan.id === 'negocio' ? '50' : '200'} imágenes extra de bienvenida
                      </p>
                    )}
                  </>
                ) : !isArgentina && (plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) ? (
                  <>
                    <p className="text-[17px] font-bold text-brand-black/90">
                      <span className="flex items-center justify-start gap-2">
                        <span className="text-[13px] font-normal text-neutral-400 line-through">
                          {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                        </span>
                        <span className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm bg-brand-primary">50% OFF</span>
                      </span>
                      <span className="block">
                        {formatPrice(Math.floor(getPlanPrice(effectivePlanId(plan.id), plan.price) * 0.5))}
                        <span className="text-[13px] font-normal text-neutral-500"> el primer mes</span>
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[17px] font-bold text-brand-black/90">
                      {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                      <span className="text-[13px] font-normal text-neutral-500">/mes</span>
                    </p>
                    {isArgentina && plan.id === 'emprendimiento' && isUpgrade(plan.id) && (
                      <p className="text-[11px] text-neutral-400 mt-1">
                        Incluye 30 imágenes extra el primer mes
                      </p>
                    )}
                  </>
                )}
                {isArgentina && !appliedCoupon && (plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) && (
                  <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-brand-primary/10">
                    <Gift className="w-3.5 h-3.5 text-brand-primary flex-shrink-0" />
                    <span className="text-[11px] font-semibold text-brand-primary">+{plan.id === 'negocio' ? '50' : '200'} imágenes extra de bienvenida</span>
                  </div>
                )}
              </div>
              )}
              
              <div className="mt-auto">
              {/* Ultra tier dropdown */}
              {plan.id === 'enterprise' && (
                <div ref={ultraTierRefMobile} className="relative mb-3">
                  <button
                    type="button"
                    onClick={() => setUltraTierOpen(!ultraTierOpen)}
                    className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated transition-colors"
                  >
                    <span className="text-brand-black/90 font-medium">{ultraTiers[ultraTierIndex].label}</span>
                    <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${ultraTierOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {ultraTierOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1">
                      {ultraTiers.map((tier, idx) => (
                        <button
                          key={tier.planId}
                          type="button"
                          onClick={() => { setUltraTierIndex(idx); setUltraTierOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-[15px] transition-colors ${
                            ultraTierIndex === idx ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
                          }`}
                        >
                          {tier.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {plan.id === 'experto' ? (
                <a
                  href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Empresa.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                >
                  Saber más
                </a>
              ) : (
                <button
                  onClick={() => handleSubscription(plan.id)}
                  disabled={processing || isCurrentPlan(plan.id)}
                  className={`inline-flex items-center justify-center w-full h-10 px-4 rounded-md text-[15px] font-medium transition-colors ${
                    isCurrentPlan(plan.id)
                      ? 'bg-green-600 text-white cursor-default shadow-sm' 
                      : isDowngrade(plan.id)
                      ? 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                      : 'bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm'
                  }`}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : isCurrentPlan(plan.id) ? (
                    'Plan Actual'
                  ) : currentPlan === 'gratis' ? (
                    'Subscribirse'
                  ) : isDowngrade(plan.id) ? (
                    'Reducir plan'
                  ) : (
                    'Mejorar plan'
                  )}
                </button>
              )}
              {plan.id === 'experto' && (
                <button
                  type="button"
                  onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Empresa.`)}
                  className="hidden md:inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                >
                  Saber más
                </button>
              )}
              {isCurrentPlan(plan.id) && currentPlan !== 'gratis' && plan.id !== 'experto' && (
                <button
                  onClick={handleOpenCancelModal}
                  disabled={cancellingSubscription || cancelSuccess}
                  className={`mt-2 flex items-center justify-center w-full h-10 px-4 rounded-md text-[13px] font-medium transition-colors ${
                    cancelSuccess 
                      ? 'bg-green-50 border border-green-200 text-green-700 cursor-default' 
                      : 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                  }`}
                >
                  {cancellingSubscription ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : cancelSuccess ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Cancelada
                    </>
                  ) : (
                    'Cancelar suscripción'
                  )}
                </button>
              )}
              </div>
              </div>
              
              <hr className="border-neutral-200 mt-5 mb-5" />
              
              <ul className="flex-grow">
                {plan.features.map((feature, index) => {
                  const isDisabled = feature.startsWith('~');
                  const displayFeature = isDisabled ? feature.slice(1) : feature;
                  return (
                    <li key={index} className={`flex items-start mb-2 ${isDisabled ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      {featureIcon(displayFeature, isDisabled)}
                      <span className="text-[13px]">{displayFeature}</span>
                    </li>
                  );
                })}
              </ul>

              
            </div>
          </div>
              </div>
              ))}
              </div>
            </div>
            {/* Coupon section — mobile only (below cards, Argentina only) */}
            {isArgentina && <div className="md:hidden mt-4 mx-0.5">
              {appliedCoupon && appliedCoupon.es_invisible ? null : appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-surface-card border border-brand-primary/20 rounded-md">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-brand-primary" />
                    <span className="text-[13px] text-brand-black/90">
                      Cupón <strong>{appliedCoupon.codigo}</strong> — {Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF
                    </span>
                  </div>
                  <button type="button" onClick={() => setAppliedCoupon(null)} className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors">Quitar</button>
                </div>
              ) : showCouponInput ? (
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
                    placeholder="Código de cupón"
                    className="w-full h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => { setShowCouponInput(false); setCouponCode(''); setCouponError(null); }} className="flex-1 h-10 text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors rounded-md border border-neutral-200">Cancelar</button>
                    <button type="button" onClick={handleValidateCoupon} disabled={couponLoading} className="flex-1 h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-1.5">
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-[13px] text-red-600 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{couponError}</p>
                  )}
                </div>
              ) : (
                <button type="button" onClick={() => setShowCouponInput(true)} className="w-full flex items-center justify-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors">
                  <Tag className="w-3.5 h-3.5" />
                  Tengo un cupón
                </button>
              )}
            </div>}
            {/* Desktop: grid — 4 cols when 4 plans, 3 cols centered when fewer */}
            <div className="hidden md:block relative">
            <div className={`relative z-10 grid md:gap-4 pt-4 overflow-x-hidden ${filteredPlans.length >= 5 ? 'md:grid-cols-5' : filteredPlans.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 max-w-4xl mx-auto'}`}>
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`plan-card border rounded-lg w-full bg-surface-card ${isCurrentPlan(plan.id) ? 'border-brand-primary' : 'border-neutral-200'} flex flex-col relative ${appliedCoupon || !isArgentina ? 'min-h-[600px]' : ''}`}
                >
                  {(plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-[13px] font-semibold text-white shadow-sm whitespace-nowrap ${appliedCoupon?.es_invisible ? '' : 'bg-brand-primary'}`} style={appliedCoupon?.es_invisible ? { background: 'linear-gradient(135deg, #E55D35 0%, #D94825 100%)' } : undefined}>
                        {appliedCoupon?.es_invisible ? (
                          <>
                            🔥 {`${Math.round((1 - appliedCoupon.multiplicador) * 100)}% descuento `}
                          </>
                        ) : appliedCoupon ? (
                          <>
                            <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                            {`${Math.round((1 - appliedCoupon.multiplicador) * 100)}% descuento `}
                          </>
                        ) : !isArgentina ? (
                          <>
                            <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                            50% descuento primer mes
                          </>
                        ) : (
                          <>
                            <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                            {`+${plan.id === 'negocio' ? '50' : '200'} imágenes extra de bienvenida`}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <div className={`p-5 flex flex-col h-full ${(plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) ? 'pt-6' : ''}`}>
                    <div style={{ minHeight: '260px' }} className="flex flex-col">
                    <h3 className="text-[17px] font-bold text-brand-black/90">{plan.name}</h3>
                    <p className="text-[13px] text-neutral-500 mb-4 mt-1.5">{plan.description}</p>
                    {plan.id !== 'experto' && (
                    <div className="mb-5">
                      {appliedCoupon && (plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) ? (
                        <>
                          <div className="flex items-center flex-wrap gap-1.5">
                            <span className="text-[13px] font-normal text-neutral-400 line-through">
                              {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                            </span>
                            <span className={`text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm ${appliedCoupon?.es_invisible ? '' : 'bg-brand-primary'}`} style={appliedCoupon?.es_invisible ? { background: '#E55D35' } : undefined}>{Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF</span>
                          </div>
                          <p className="text-[15px] font-bold text-brand-black/90">
                            {formatPrice(Math.floor(getPlanPrice(effectivePlanId(plan.id), plan.price) * appliedCoupon.multiplicador))}
                            <span className="text-[11px] font-normal text-neutral-500"> el primer mes</span>
                          </p>
                          {isArgentina && (
                            <p className="text-[11px] text-neutral-400 mt-1">
                              + {plan.id === 'negocio' ? '50' : '200'} imágenes extra de bienvenida
                            </p>
                          )}
                        </>
                      ) : !isArgentina && (plan.id === 'negocio' || isEnterprisePlan(plan.id)) && isUpgrade(plan.id) ? (
                        <>
                          <div className="flex items-center flex-wrap gap-1.5">
                            <span className="text-[13px] font-normal text-neutral-400 line-through">
                              {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                            </span>
                            <span className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-sm bg-brand-primary">50% OFF</span>
                          </div>
                          <p className="text-[15px] font-bold text-brand-black/90">
                            {formatPrice(Math.floor(getPlanPrice(effectivePlanId(plan.id), plan.price) * 0.5))}
                            <span className="text-[11px] font-normal text-neutral-500"> el primer mes</span>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-[17px] font-bold text-brand-black/90">
                            {formatPrice(getPlanPrice(effectivePlanId(plan.id), plan.price))}
                            <span className="text-[13px] font-normal text-neutral-500">/mes</span>
                          </p>
                          {isArgentina && plan.id === 'emprendimiento' && isUpgrade(plan.id) && (
                            <p className="text-[11px] text-neutral-400 mt-1">
                              Incluye 30 imágenes extra el primer mes
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    )}
                    <div className="mt-auto">
                    {/* Ultra tier dropdown (desktop) */}
                    {plan.id === 'enterprise' && (
                      <div ref={ultraTierRefDesktop} className="relative mb-3">
                        <button
                          type="button"
                          onClick={() => setUltraTierOpen(!ultraTierOpen)}
                          className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated transition-colors"
                        >
                          <span className="text-brand-black/90 font-medium">{ultraTiers[ultraTierIndex].label}</span>
                          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${ultraTierOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {ultraTierOpen && (
                          <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1">
                            {ultraTiers.map((tier, idx) => (
                              <button
                                key={tier.planId}
                                type="button"
                                onClick={() => { setUltraTierIndex(idx); setUltraTierOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[15px] transition-colors ${
                                  ultraTierIndex === idx ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
                                }`}
                              >
                                {tier.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {plan.id === 'experto' ? (
                      <button
                        type="button"
                        onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Quisiera saber sobre la contratación del plan Empresa.`)}
                        className="inline-flex items-center justify-center w-full h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors"
                      >
                        Saber más
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscription(plan.id)}
                        disabled={processing || isCurrentPlan(plan.id)}
                        className={`inline-flex items-center justify-center w-full h-10 px-4 rounded-md text-[15px] font-medium transition-colors ${
                          isCurrentPlan(plan.id)
                            ? 'bg-green-600 text-white cursor-default shadow-sm'
                            : isDowngrade(plan.id)
                            ? 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                            : 'bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm'
                        }`}
                      >
                        {processing ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : isCurrentPlan(plan.id) ? (
                          'Plan Actual'
                        ) : currentPlan === 'gratis' ? (
                          'Subscribirse'
                        ) : isDowngrade(plan.id) ? (
                          'Reducir plan'
                        ) : (
                          'Mejorar plan'
                        )}
                      </button>
                    )}
                    {isCurrentPlan(plan.id) && currentPlan !== 'gratis' && plan.id !== 'experto' && (
                      <button
                        onClick={handleOpenCancelModal}
                        disabled={cancellingSubscription || cancelSuccess}
                        className={`mt-2 flex items-center justify-center w-full h-10 px-4 rounded-md text-[13px] font-medium transition-colors ${
                          cancelSuccess
                            ? 'bg-green-50 border border-green-200 text-green-700 cursor-default'
                            : 'bg-surface-elevated border border-neutral-200 hover:bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {cancellingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : cancelSuccess ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Cancelada
                          </>
                        ) : (
                          'Cancelar suscripción'
                        )}
                      </button>
                    )}
                    </div>
                    </div>
                    
                    <hr className="border-neutral-200 mt-5 mb-5" />
                    
                    <ul className="flex-grow">
                      {plan.features.map((feature, index) => {
                        const isDisabled = feature.startsWith('~');
                        const displayFeature = isDisabled ? feature.slice(1) : feature;
                        return (
                          <li key={index} className={`flex items-start mb-2 ${isDisabled ? 'text-neutral-300' : 'text-neutral-600'}`}>
                            {featureIcon(displayFeature, isDisabled)}
                            <span className="text-[13px]">{displayFeature}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </>
        );
      })()}
      
      
      {/* Mobile: WhatsApp help button centered — before info section */}
      <div className="mt-8 flex justify-center md:hidden">
        <a
          href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-surface-card border border-neutral-200 text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 shadow-sm transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Tenés alguna duda?
        </a>
      </div>

      <div className="mt-6 md:mt-12 flex flex-col md:flex-row md:items-start md:justify-between gap-6 md:pr-32">
        <div className="max-w-2xl mx-auto md:mx-0">
          <p className="text-[11px] text-neutral-400 text-center md:text-left mb-3 uppercase tracking-wider font-medium">Información importante</p>
          <div className="grid grid-cols-1 gap-2.5">
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Las suscripciones se renuevan automáticamente cada mes.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Puedes cancelar tu suscripción en cualquier momento.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Al cambiar de plan, se aplicará el nuevo límite de fotos inmediatamente.</span>
            </div>
            <div className="flex items-start gap-2.5 text-[13px] text-neutral-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300 mt-0.5 flex-shrink-0" />
              <span>Los pagos son procesados de forma segura por {providerName}.</span>
            </div>
          </div>
        </div>

        {/* Desktop: help button to the right of info section */}
        <button
          type="button"
          onClick={() => setShowContactQR(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}
          className="hidden md:inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-surface-card border border-neutral-200 text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 shadow-sm transition-colors flex-shrink-0 self-center"
        >
          <MessageCircle className="w-4 h-4" />
          Tenés alguna duda?
        </button>
      </div>
      
      {/* Cancel Subscription Multi-Step Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50">
          {/* Mobile: solid bg / Desktop: dark translucent overlay */}
          <div className="absolute inset-0 bg-surface-bg md:bg-black/50 md:backdrop-blur-sm" onClick={handleCloseCancelModal} />
          {/* Scrollable content layer */}
          <div className="relative z-10 flex items-start md:items-center justify-center w-full h-full md:p-4 overflow-y-auto">
          <div className="bg-surface-bg md:bg-surface-card md:rounded-lg md:shadow-md md:max-w-md w-full relative min-h-screen md:min-h-0 md:my-0 pt-12 md:pt-0 flex flex-col md:min-h-[420px]">

            {/* Close button (hidden on success step) */}
            {cancelStep !== 4 && (
              <button 
                onClick={handleCloseCancelModal}
                className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors z-10"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            {/* ── Step 1: Reason selection ── */}
            {cancelStep === 1 && (
              <div className="px-4 md:px-0">
                <div className="px-1 md:px-5 pt-5 pb-3 text-center">
                  <p className="text-[17px] font-semibold text-brand-black/90 mb-1">Lamentamos que te tengas que ir</p>
                  <p className="text-[13px] text-neutral-500 leading-relaxed">
                    Quisiéramos entender por qué te vas para mejorar la plataforma y que cumpla y supere tus expectativas.
                  </p>
                </div>
                <div className="bg-surface-card rounded-[13px] overflow-hidden">
                  {cancelReasons.map((reason, i) => (
                    <div key={i}>
                      {i > 0 && <div className="ml-4 h-px bg-black/[0.06]" />}
                      <button
                        type="button"
                        onClick={() => {
                          setCancelMotivo(reason);
                          setCancelStep(2);
                        }}
                        className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left hover:bg-surface-elevated active:bg-surface-elevated transition-colors"
                      >
                        <span className="text-[15px] text-brand-black/90 pr-3">{reason}</span>
                        <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="pb-5 pt-3 px-1 md:px-5">
                  <button
                    onClick={handleCloseCancelModal}
                    className="w-full h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 2: Detail text ── */}
            {cancelStep === 2 && (
              <div className="p-5 flex flex-col flex-1">
                <div>
                  <button
                    type="button"
                    onClick={() => { setCancelStep(1); setCancelDetalle(''); }}
                    className="hidden md:flex text-[13px] text-brand-primary font-medium mb-4 items-center gap-0.5 hover:text-brand-primary-hover transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                    Volver
                  </button>
                  <p className="text-[17px] font-semibold text-brand-black/90 mb-1">¿Podrías contarnos por qué?</p>
                  <p className="text-[13px] text-neutral-500 mb-4">
                    Motivo: <span className="font-medium text-brand-black/70">{cancelMotivo}</span>
                  </p>
                  <textarea
                    value={cancelDetalle}
                    onChange={(e) => setCancelDetalle(e.target.value)}
                    placeholder="Contanos un poco más..."
                    className="w-full h-32 px-3 py-2.5 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-none placeholder:text-neutral-400"
                    autoFocus
                  />
                </div>
                <button
                  onClick={() => setCancelStep(3)}
                  disabled={cancelDetalle.trim().length === 0}
                  className={`w-full mt-auto h-10 px-4 rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center ${
                    cancelDetalle.trim().length > 0
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  Siguiente
                </button>
              </div>
            )}

            {/* ── Step 3: Final confirmation ── */}
            {cancelStep === 3 && (
              <div className="p-5 flex flex-col flex-1">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-4">
                      <Ban className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">¿Estás seguro?</h3>
                    <p className="text-[15px] text-neutral-500">
                      Al confirmar, tu suscripción se cancelará y perderás acceso a las funciones premium al finalizar tu período actual.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2.5 pt-4">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancellingSubscription}
                    className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center"
                  >
                    {cancellingSubscription ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Cancelar suscripción'
                    )}
                  </button>
                  <button
                    onClick={() => setCancelStep(2)}
                    disabled={cancellingSubscription}
                    className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors"
                  >
                    Volver
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 4: Success ── */}
            {cancelStep === 4 && (
              <div className="p-5 md:p-5 text-center">
                <div className="mx-auto w-12 h-12 rounded-md bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">Cancelación exitosa</h3>
                <p className="text-[15px] text-neutral-500 mb-1">
                  Tu suscripción ha sido cancelada.
                </p>
                <p className="text-[13px] text-neutral-500 leading-relaxed">
                  Podrás seguir usando todas las funciones de tu plan actual hasta el <strong className="text-brand-black/80">{cancelSuccessDate}</strong>.
                </p>
                <button
                  onClick={() => {
                    handleCloseCancelModal();
                    fetchUserProfile();
                  }}
                  className="w-full mt-5 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                >
                  Entendido
                </button>
              </div>
            )}

          </div>
          </div>
        </div>
      )}
      
      </>)}

      {/* Contact QR Modal - Desktop only (outside fechaCancelacion wrapper so it works from both views) */}
      {showContactQR && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => setShowContactQR('')}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1.5 text-center">Contactanos por WhatsApp</h3>
            <p className="text-[13px] text-neutral-500 mb-4 text-center">Escaneá el código QR con tu celular para abrir WhatsApp</p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://wa.me/5491122635392?text=${encodeURIComponent(showContactQR)}`)}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48 rounded-md"
              />
            </div>
            <button
              onClick={() => setShowContactQR('')}
              className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Paywall;
